import { makeAutoObservable } from 'mobx';

import type { LogCurve } from '../../../shared/api/IWellDirectoryService';
import { BaseParameterTypes } from '../../../shared/baseParams';
import type { IClassOfUnitsService } from '../../../shared/IClassOfUnitsService';
import type { UnitManager } from '../../../shared/services/unit-manager';
import type { DepthUnits } from '../../../shared/types';
import { clamp } from '../../components/HeatMapChart/utils';
import { DEFAULT_MODES, DEPTH_MAX, DEPTH_MIN } from '../../const';
import type { HeatmapWidgetStateParams } from '../../types';
import type { HeatmapWidgetStatePublisher } from '../HeatmapWidgetStatePublisher';
import { HeatmapChartKey, IntervalsTypes, OperationRegimes, StepRangeKey, ZFilterKey } from '../types';
import type {
  AnalysisType,
  AnalysisTypeKey,
  DrillingMode,
  HeatmapZFilterParams,
  HeatmapWidgetAxisParams,
  UnitOptionsKeys,
  ValueRangeKey,
  VisualMapAxisParams,
} from '../types';

import { restoreAxisParams, serializeAxisParams } from './settingsPersistence';
import {
  AXIS_LABEL_KEY_MAP,
  ANALYSIS_AXIS_MAP,
  createAxisParamsFromConfig,
  createZFilterParamsFromConfig,
  CURVE_AXIS_CONFIG,
  STEP_RANGE_AXIS_MAP,
  UNIT_AXIS_MAP,
  UNIT_OPTIONS_CONFIG_MAP,
  VALUE_RANGE_AXIS_MAP,
  Z_FILTER_AXIS_MAP,
  Z_FILTER_KEYS,
} from './settingsStoreConfig';
import type { AxisKey, AxisParamsByKey, VisualMapAxisKey, XAxisKey, YAxisKey } from './settingsStoreConfig';

const LAST_BINS_RANGE_MIN = 1;
const LAST_BINS_RANGE_LIMITS: Record<DepthUnits, number> = {
  m: 100,
  ft: 300,
};

type DepthRangeState = {
  start: number;
  end: number | null;
};

type DrillingModesState = {
  rotor: DrillingMode;
  slide: DrillingMode;
};

type DrillingState = {
  interval: IntervalsTypes;
  depthRange: DepthRangeState;
  lastBinsRange: number;
  operationRegimes: OperationRegimes;
  modes: DrillingModesState;
};

export class HeatmapWidgetSettingsStore {
  axisParams: AxisParamsByKey = createAxisParamsFromConfig();
  private drillingState: DrillingState = {
    interval: IntervalsTypes.USER_DEFINED,
    depthRange: {
      start: DEPTH_MIN,
      end: DEPTH_MAX,
    },
    lastBinsRange: LAST_BINS_RANGE_LIMITS.m,
    operationRegimes: OperationRegimes.ROTATION,
    modes: {
      rotor: DEFAULT_MODES.rotor,
      slide: DEFAULT_MODES.slide,
    },
  };

  depthUnit: DepthUnits;

  curvesData: Partial<Record<BaseParameterTypes, LogCurve>> | null = null;

  chartVisibility: Record<HeatmapChartKey, boolean> = {
    [HeatmapChartKey.ROP]: true,
    [HeatmapChartKey.DENSITY]: false,
    [HeatmapChartKey.MSE]: false,
  };

  zFilterParams: Record<ZFilterKey, HeatmapZFilterParams> = createZFilterParamsFromConfig();

  private statePublisher: HeatmapWidgetStatePublisher;

  constructor(
    private classOfUnitsService: IClassOfUnitsService,
    private unitManager: UnitManager,
    private isAltNames: boolean,
    defaultDepthUnits: DepthUnits,
    statePublisher: HeatmapWidgetStatePublisher,
    initialState: Partial<HeatmapWidgetStateParams>,
  ) {
    this.statePublisher = statePublisher;
    this.depthUnit = defaultDepthUnits;
    this.drillingState.lastBinsRange = LAST_BINS_RANGE_LIMITS[defaultDepthUnits];

    this.applyInitialState(initialState);
    makeAutoObservable(this);
  }

  get operationRegimes(): OperationRegimes {
    return this.drillingState.operationRegimes;
  }

  get wobAxisParams(): HeatmapWidgetAxisParams {
    return this.axisParams.wobAxisParams;
  }

  get rpmAxisParams(): HeatmapWidgetAxisParams {
    return this.axisParams.rpmAxisParams;
  }

  get rpmtotAxisParams(): HeatmapWidgetAxisParams {
    return this.axisParams.rpmtotAxisParams;
  }

  get dpressAxisParams(): HeatmapWidgetAxisParams {
    return this.axisParams.dpressAxisParams;
  }

  get ropAxisParams(): VisualMapAxisParams {
    return this.axisParams.ropAxisParams;
  }

  get mseBitAxisParams(): VisualMapAxisParams {
    return this.axisParams.mseBitAxisParams;
  }

  get mseSurfaceAxisParams(): VisualMapAxisParams {
    return this.axisParams.mseSurfaceAxisParams;
  }
  get interval(): IntervalsTypes {
    return this.drillingState.interval;
  }

  get startDepth() {
    return this.drillingState.depthRange.start;
  }

  get endDepth() {
    return this.drillingState.depthRange.end;
  }

  get rotorDrilling(): DrillingMode {
    return this.drillingState.modes.rotor;
  }

  get slideDrilling(): DrillingMode {
    return this.drillingState.modes.slide;
  }

  get lastBinsRange(): number {
    return this.drillingState.lastBinsRange;
  }

  get lastBinsRangeMax(): number {
    return LAST_BINS_RANGE_LIMITS[this.depthUnit];
  }

  get zFilterValues(): Record<ZFilterKey, HeatmapZFilterParams> {
    return this.zFilterParams;
  }

  get currentZFilterParams(): {
    rop: HeatmapZFilterParams;
    mse: HeatmapZFilterParams;
  } {
    const isRotation = this.operationRegimes === OperationRegimes.ROTATION;
    return {
      rop: this.zFilterParams[isRotation ? ZFilterKey.ROTATION_ROP : ZFilterKey.SLIDE_ROP],
      mse: this.zFilterParams[isRotation ? ZFilterKey.ROTATION_MSE : ZFilterKey.SLIDE_MSE],
    };
  }

  unitOptions(key: UnitOptionsKeys) {
    const config = UNIT_OPTIONS_CONFIG_MAP[key];
    if (!config) {
      return [];
    }

    const curve = this.curvesData?.[config.parameterType];

    const unitClass = curve ? this.classOfUnitsService.findClassById(curve.parameter.classOfUnitId) : null;
    const options = unitClass
      ? unitClass.units.map((unit) => ({
          label: unit.abbreviation,
          value: unit.id,
        }))
      : [];

    this.addUnitOptionIfMissing(options, curve?.unit);
    return options;
  }

  get unitSettings(): Record<UnitOptionsKeys, { axisKey: AxisKey; unit: HeatmapWidgetAxisParams['unit'] }> {
    return (Object.keys(UNIT_AXIS_MAP) as UnitOptionsKeys[]).reduce(
      (acc, unitKey) => {
        const axisKey = UNIT_AXIS_MAP[unitKey];
        acc[unitKey] = {
          axisKey,
          unit: this.axisParams[axisKey].unit,
        };
        return acc;
      },
      {} as Record<UnitOptionsKeys, { axisKey: AxisKey; unit: HeatmapWidgetAxisParams['unit'] }>,
    );
  }

  setInterval(interval: IntervalsTypes) {
    if (!Object.values(IntervalsTypes).includes(interval)) {
      return;
    }
    this.drillingState.interval = interval;
    this.statePublisher.setParams({ interval });
  }

  setRange(key: ValueRangeKey, params: Partial<HeatmapWidgetAxisParams>) {
    return this.updateAxis(VALUE_RANGE_AXIS_MAP[key], params);
  }

  get rangeValues(): Record<ValueRangeKey, { min: number; max: number }> {
    return {
      wob: {
        min: this.wobAxisParams.min,
        max: this.wobAxisParams.max,
      },
      dpress: {
        min: this.dpressAxisParams.min,
        max: this.dpressAxisParams.max,
      },
      rpm: {
        min: this.rpmAxisParams.min,
        max: this.rpmAxisParams.max,
      },
      rpmtot: {
        min: this.rpmtotAxisParams.min,
        max: this.rpmtotAxisParams.max,
      },
    };
  }

  get analysisSettings(): Record<AnalysisTypeKey, { axisKey: VisualMapAxisKey; params: VisualMapAxisParams }> {
    return (Object.keys(ANALYSIS_AXIS_MAP) as AnalysisTypeKey[]).reduce(
      (acc, analysisKey) => {
        const axisKey = ANALYSIS_AXIS_MAP[analysisKey];
        acc[analysisKey] = {
          axisKey,
          params: this.axisParams[axisKey] as VisualMapAxisParams,
        };
        return acc;
      },
      {} as Record<AnalysisTypeKey, { axisKey: VisualMapAxisKey; params: VisualMapAxisParams }>,
    );
  }

  get analysisTypeValues(): Record<AnalysisTypeKey, { type: AnalysisType; unit: number | null }> {
    return (Object.keys(ANALYSIS_AXIS_MAP) as AnalysisTypeKey[]).reduce(
      (acc, analysisKey) => {
        const { params } = this.analysisSettings[analysisKey];
        acc[analysisKey] = {
          type: params.analysisType,
          unit: params.unit.id,
        };
        return acc;
      },
      {} as Record<AnalysisTypeKey, { type: AnalysisType; unit: number | null }>,
    );
  }

  get visibleChartsCount() {
    return Object.values(this.chartVisibility).filter(Boolean).length;
  }

  get allChartsEnabled() {
    return this.visibleChartsCount === Object.keys(this.chartVisibility).length;
  }

  get stepValues(): Record<StepRangeKey, { step: number; unit: number | null }> {
    return {
      wob: { step: this.wobAxisParams.step, unit: this.wobAxisParams.unit.id },
      dpress: {
        step: this.dpressAxisParams.step,
        unit: this.dpressAxisParams.unit.id,
      },
      rpm: { step: this.rpmAxisParams.step, unit: this.rpmAxisParams.unit.id },
      rpmtot: {
        step: this.rpmtotAxisParams.step,
        unit: this.rpmtotAxisParams.unit.id,
      },
    };
  }

  setStep(key: StepRangeKey, params: Partial<HeatmapWidgetAxisParams>) {
    return this.updateAxis(STEP_RANGE_AXIS_MAP[key], params);
  }

  setUnit(key: UnitOptionsKeys, unit: { id: number; abbreviation: string }) {
    return this.updateAxis(UNIT_AXIS_MAP[key], { unit });
  }

  setAnalysisType(key: AnalysisTypeKey, type: AnalysisType) {
    return this.updateAxis(ANALYSIS_AXIS_MAP[key], { analysisType: type });
  }

  updateAxis<Key extends AxisKey>(axisKey: Key, params: Partial<AxisParamsByKey[Key]>) {
    this.patchAxis(axisKey, params);
    this.statePublisher.setParams(serializeAxisParams(this.axisParams, params.unit ? [axisKey] : []));
  }

  setDepthRange({ startDepth, endDepth }: { startDepth?: number; endDepth?: number | null }) {
    const start = startDepth ?? this.startDepth;
    const end = endDepth === undefined ? this.endDepth : endDepth;
    this.drillingState.depthRange = { start, end };
    this.statePublisher.setParams({ startDepth: start, endDepth: end });
  }

  setRotorDrilling(drilling: DrillingMode) {
    if (drilling !== 'load' && drilling !== 'drop_off') {
      return;
    }
    this.drillingState.modes.rotor = drilling;
    this.statePublisher.setParams({ rotorDrilling: drilling });
  }

  setSlideDrilling(drilling: DrillingMode) {
    if (drilling !== 'load' && drilling !== 'drop_off') {
      return;
    }
    this.drillingState.modes.slide = drilling;
    this.statePublisher.setParams({ slideDrilling: drilling });
  }

  setCurvesData(curvesData: Partial<Record<BaseParameterTypes, LogCurve>> | null) {
    this.curvesData = curvesData;
    this.applyCurveAxisValues(curvesData);
  }

  setOperationRegimes(operationRegimes: OperationRegimes) {
    if (!Object.values(OperationRegimes).includes(operationRegimes)) {
      return;
    }
    this.drillingState.operationRegimes = operationRegimes;
    this.statePublisher.setParams({ operationRegimes });
  }

  setLastBinsRange(value: number) {
    this.drillingState.lastBinsRange = value;
    this.statePublisher.setParams({
      lastBinsRange: this.drillingState.lastBinsRange,
    });
  }

  setZFilter(key: ZFilterKey, params: Partial<HeatmapZFilterParams>) {
    this.patchZFilter(key, params);
    this.statePublisher.setParams({
      zFilters: this.getZFiltersStateForPublish(),
    });
  }

  private get currentAxisKeys(): {
    x: XAxisKey;
    y: YAxisKey;
    mse: VisualMapAxisKey;
  } {
    const xKey: XAxisKey = this.operationRegimes === OperationRegimes.ROTATION ? 'rpmAxisParams' : 'rpmtotAxisParams';
    const drillingMode = this.operationRegimes === OperationRegimes.ROTATION ? this.rotorDrilling : this.slideDrilling;
    const yKey: YAxisKey = drillingMode === 'load' ? 'wobAxisParams' : 'dpressAxisParams';
    const mseKey: VisualMapAxisKey =
      this.operationRegimes === OperationRegimes.ROTATION ? 'mseSurfaceAxisParams' : 'mseBitAxisParams';
    return { x: xKey, y: yKey, mse: mseKey };
  }

  get currentAxes() {
    const keys = this.currentAxisKeys;
    return {
      x: {
        key: keys.x,
        params: this.axisParams[keys.x],
      },
      y: {
        key: keys.y,
        params: this.axisParams[keys.y],
      },
      mse: {
        key: keys.mse,
        params: this.axisParams[keys.mse],
      },
    };
  }

  get currentXAxisKey(): XAxisKey {
    return this.currentAxisKeys.x;
  }

  get currentYAxisKey(): YAxisKey {
    return this.currentAxisKeys.y;
  }

  get currentMseAxisKey(): VisualMapAxisKey {
    return this.currentAxisKeys.mse;
  }

  get currentXAxisParams(): HeatmapWidgetAxisParams {
    return this.currentAxes.x.params;
  }

  get currentYAxisParams(): HeatmapWidgetAxisParams {
    return this.currentAxes.y.params;
  }

  get currentMseAxisParams(): VisualMapAxisParams {
    return this.currentAxes.mse.params;
  }

  get currentXAxisParameter(): BaseParameterTypes {
    return this.operationRegimes === OperationRegimes.ROTATION
      ? BaseParameterTypes.ROTARY_SPEED
      : BaseParameterTypes.BIT_SPEED;
  }

  setAltNames(value: boolean) {
    this.isAltNames = value;
    this.applyCurveAxisValues(this.curvesData);
  }

  get currentYAxisParameter(): BaseParameterTypes {
    const drillingMode = this.operationRegimes === OperationRegimes.ROTATION ? this.rotorDrilling : this.slideDrilling;
    return drillingMode === 'load' ? BaseParameterTypes.WEIGHT_ON_BIT : BaseParameterTypes.BIT_CONTACT_PRESSURE;
  }

  get currentMseAxisParameter(): BaseParameterTypes {
    return this.operationRegimes === OperationRegimes.ROTATION
      ? BaseParameterTypes.MECH_SPEC_ENERGY_SURFACE
      : BaseParameterTypes.MECH_SPEC_ENERGY_BIT;
  }

  get currentXAxisLabelKey(): string {
    return AXIS_LABEL_KEY_MAP[this.currentXAxisKey];
  }

  get currentYAxisLabelKey(): string {
    return AXIS_LABEL_KEY_MAP[this.currentYAxisKey];
  }

  get currentMseAxisLabelKey(): string {
    return AXIS_LABEL_KEY_MAP[this.currentMseAxisKey];
  }

  getRangeDisplayName(valueRangeKey: ValueRangeKey): string | null {
    if (!this.isAltNames) {
      return null;
    }

    const axisKey = VALUE_RANGE_AXIS_MAP[valueRangeKey];
    return this.axisParams[axisKey].name;
  }

  getStepDisplayName(stepKeys: StepRangeKey[]): string | null {
    if (!this.isAltNames) {
      return null;
    }

    const currentStepKey = this.operationRegimes === OperationRegimes.ROTATION ? StepRangeKey.RPM : StepRangeKey.RPMTOT;
    const preferredStepKey = stepKeys.includes(currentStepKey) ? currentStepKey : stepKeys[0];
    const axisKey = STEP_RANGE_AXIS_MAP[preferredStepKey];

    return this.axisParams[axisKey].name;
  }

  getAnalysisDisplayName(analysisKey: AnalysisTypeKey): string | null {
    if (!this.isAltNames) {
      return null;
    }

    const axisKey = ANALYSIS_AXIS_MAP[analysisKey];
    return this.axisParams[axisKey].name;
  }

  getZFilterDisplayName(filterKey: ZFilterKey): string | null {
    if (!this.isAltNames) {
      return null;
    }

    const axisKey = Z_FILTER_AXIS_MAP[filterKey];
    return this.axisParams[axisKey].name;
  }

  isChartVisible(key: HeatmapChartKey) {
    return this.chartVisibility[key];
  }

  setChartVisibility(key: HeatmapChartKey, isVisible: boolean) {
    if (!Object.values(HeatmapChartKey).includes(key)) {
      return;
    }
    const nextValue = Boolean(isVisible);
    const isLastEnabled = this.visibleChartsCount <= 1 && this.chartVisibility[key];
    if (!nextValue && isLastEnabled) {
      return;
    }
    this.chartVisibility = {
      ...this.chartVisibility,
      [key]: nextValue,
    };
    this.statePublisher.setParams({
      chartVisibility: { ...this.chartVisibility },
    });
  }

  toggleChartVisibility(key: HeatmapChartKey) {
    this.setChartVisibility(key, !this.chartVisibility[key]);
  }

  private getZFiltersStateForPublish(): Record<ZFilterKey, HeatmapZFilterParams> {
    return Z_FILTER_KEYS.reduce(
      (acc, key) => {
        acc[key] = { ...this.zFilterParams[key] };
        return acc;
      },
      {} as Record<ZFilterKey, HeatmapZFilterParams>,
    );
  }

  private patchZFilter(key: ZFilterKey, params: Partial<HeatmapZFilterParams>) {
    this.zFilterParams[key] = {
      ...this.zFilterParams[key],
      ...params,
    };
  }

  private patchAxis<Key extends AxisKey>(axisKey: Key, params: Partial<AxisParamsByKey[Key]>) {
    this.axisParams[axisKey] = {
      ...this.axisParams[axisKey],
      ...params,
    };
  }

  private getCurveDisplayName(curve: LogCurve): string {
    if (this.isAltNames && curve.parameter.generalName) {
      return curve.parameter.generalName;
    }

    return curve.parameter.displayMnemonic;
  }

  private applyCurveAxisValues(curvesData: Partial<Record<BaseParameterTypes, LogCurve>> | null) {
    CURVE_AXIS_CONFIG.forEach(({ parameterType, axisKey }) => {
      const curve = curvesData?.[parameterType];
      if (!curve) {
        return;
      }
      const hasUnitFromSettings = this.axisParams[axisKey].unit.id !== null;
      const patch: Partial<AxisParamsByKey[typeof axisKey]> = {
        name: this.getCurveDisplayName(curve),
      };

      if (!hasUnitFromSettings) {
        patch.unit = curve.unit;
      }

      this.patchAxis(axisKey, patch);
    });
  }

  private addUnitOptionIfMissing(
    options: { label: string; value: number }[],
    unit?: { id: number | null; abbreviation: string },
  ) {
    if (!unit?.id) {
      return;
    }
    const hasUnit = options.some((option) => option.value === unit.id);
    if (!hasUnit) {
      options.push({
        label: unit.abbreviation,
        value: unit.id,
      });
    }
  }

  private applyInitialState(state: Partial<HeatmapWidgetStateParams>) {
    const previousUnit = this.depthUnit;
    if (state.depthUnit === 'm' || state.depthUnit === 'ft') {
      this.depthUnit = state.depthUnit;
    }
    const defaultEnd = Math.round(this.unitManager.convert(previousUnit, this.depthUnit, DEPTH_MAX) * 10) / 10;
    const start = state.startDepth ?? DEPTH_MIN;
    const end = state.endDepth === undefined ? defaultEnd : state.endDepth;
    this.drillingState.depthRange = { start, end };
    const lastBinsRange =
      state.lastBinsRange ?? Math.round(this.unitManager.convert(previousUnit, this.depthUnit, this.lastBinsRange));
    this.drillingState.lastBinsRange = lastBinsRange;
    if (state.interval && Object.values(IntervalsTypes).includes(state.interval)) {
      this.drillingState.interval = state.interval;
    }
    if (state.operationRegimes && Object.values(OperationRegimes).includes(state.operationRegimes)) {
      this.drillingState.operationRegimes = state.operationRegimes;
    }
    if (state.rotorDrilling === 'load' || state.rotorDrilling === 'drop_off') {
      this.drillingState.modes.rotor = state.rotorDrilling;
    }
    if (state.slideDrilling === 'load' || state.slideDrilling === 'drop_off') {
      this.drillingState.modes.slide = state.slideDrilling;
    }
    this.axisParams = restoreAxisParams(state);
    for (const key of Object.values(HeatmapChartKey)) {
      const visible = state.chartVisibility?.[key];
      if (typeof visible === 'boolean') {
        this.chartVisibility[key] = visible;
      }
    }
    if (!this.visibleChartsCount) {
      this.chartVisibility.rop = true;
    }
    for (const key of Z_FILTER_KEYS) {
      const filter = state.zFilters?.[key];
      if (filter) {
        this.zFilterParams[key] = { ...filter };
      }
    }
    // Hydration normalizes one complete snapshot and never emits user changes.
    this.statePublisher.replaceParams({
      ...serializeAxisParams(this.axisParams),
      depthUnit: this.depthUnit,
      startDepth: this.startDepth,
      endDepth: this.endDepth,
      lastBinsRange: this.lastBinsRange,
      interval: this.interval,
      operationRegimes: this.operationRegimes,
      rotorDrilling: this.rotorDrilling,
      slideDrilling: this.slideDrilling,
      chartVisibility: { ...this.chartVisibility },
      zFilters: this.getZFiltersStateForPublish(),
    });
  }

  setDepthUnit(unit: DepthUnits) {
    if (unit !== 'm' && unit !== 'ft') {
      return;
    }
    const previousDepthUnit = this.depthUnit;
    this.depthUnit = unit;
    if (previousDepthUnit !== unit) {
      const startConverted = this.unitManager.convert(previousDepthUnit, unit, this.drillingState.depthRange.start);
      this.drillingState.depthRange.start = Math.round(startConverted * 10) / 10;
      if (this.drillingState.depthRange.end !== null) {
        const endConverted = this.unitManager.convert(previousDepthUnit, unit, this.drillingState.depthRange.end);
        this.drillingState.depthRange.end = Math.round(endConverted * 10) / 10;
      }

      const convertedLastBinsRange = Math.round(
        this.unitManager.convert(previousDepthUnit, unit, this.drillingState.lastBinsRange),
      );
      this.drillingState.lastBinsRange = clamp(
        convertedLastBinsRange,
        LAST_BINS_RANGE_MIN,
        LAST_BINS_RANGE_LIMITS[unit],
      );
    } else {
      this.drillingState.lastBinsRange = clamp(
        this.drillingState.lastBinsRange,
        LAST_BINS_RANGE_MIN,
        LAST_BINS_RANGE_LIMITS[unit],
      );
    }

    this.statePublisher.setParams({
      depthUnit: unit,
      startDepth: this.drillingState.depthRange.start,
      endDepth: this.drillingState.depthRange.end,
      lastBinsRange: this.drillingState.lastBinsRange,
    });
  }
}
